import React, { useState, useCallback } from "react";
import classNames from "classnames";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveStrategicAxes,
  useRetrieveProjects,
  useRetrieveZonesIndicators,
  useProjectStatusesRetrieval,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { useTranslation } from "react-i18next";

import { Sort } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { events } from "@planzone/events";
import { useMessage } from "@components/useMessage";
import { useModal } from "@root/client/modal";
import { Safeguard } from "@scenes/safeguard";
import { usePreferences } from "@components/preferences";
import { Filter } from "@planzone/dto";
import { ZoneAvatar } from "@common/zoneAvatar";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import { ListToggleArrow } from "@common/listToggleArrow";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { PenIcon } from "@common/penIcon";
import { TrashIcon } from "@common/trashIcon";
import { ProjectSection } from "@common/projectSection";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { ProjectCreationButton } from "./projectCreationButton";
import { Toolbar } from "./toolbar";
import { Props, SortOptions } from "./types";
import { Loader } from "./loader";
import { ExportButton } from "./exportButton";
import { isCustomCriteria, buildCustomSort } from "./sortFunctions";
import styles from "./styles.scss";

function ProjectsPage(props: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const [sort, setSort] = useState<SortOptions>(SortOptions.Default);
  const [currentSortValue, setCurrentSortValue] =
    useState<Sort>("ascendingName");
  const [currentSortState, setCurrentSortState] = useState<{
    columnKey: string | null;
    direction: "asc" | "desc" | null;
  }>({
    columnKey: null,
    direction: null,
  });

  const m: { [Key in SortOptions]: Sort } = {
    [SortOptions.Default]: "ascendingName",
    [SortOptions.AscendingAlphabeticalOrder]: "ascendingName",
    [SortOptions.DescendingAlphabeticalOrder]: "descendingName",
    [SortOptions.StartDate]: "ascendingStartDate",
    [SortOptions.EndDate]: "descendingEndDate",
    [SortOptions.CreationDate]: "ascendingCreationDate",
  };
  const { state, edit } = usePreferences();
  let { zoneProjectsFilter: f, projectListingColumnSizes } = state;
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const {
    isRequestPending: isStrategicAxesRequestPending,
    isRequestSuccess: isStrategicAxesRequestSuccess,
    isRequestFailure: isStrategicAxesRequestFailure,
    retrieveStrategicAxes,
  } = useRetrieveStrategicAxes();
  const { statuses } = useProjectStatusesRetrieval();

  if (!f.projectStatuses || f.projectStatuses.length === 0) {
    f = {
      ...f,
      projectStatuses: statuses.filter(
        (status) => status.label !== "pz_closed",
      ),
    };
  }
  const filter = {
    ...f,
    zones: [props.zone],
  };
  const {
    zones,
    isRequestPending: isZoneRequestPending,
    isRequestSuccess: isZoneRequestSuccess,
    isRequestFailure: isZoneRequestFailure,
    retrieveZonesIndicators,
  } = useRetrieveZonesIndicators(filter, "ascendingName", 1, "indicators");
  const indicators = zones[0] || {};
  const {
    index,
    projects,
    canRetrieveNextProjects,
    isRequestPending,
    retrieveProjects,
  } = useRetrieveProjects(filter, currentSortValue, 10);

  const get = () =>
    canRetrieveNextProjects &&
    !isRequestPending &&
    retrieveProjects({
      filter,
      sort: currentSortValue,
      index,
      size: 10,
    });

  const setFilter = (nextFilter: Filter) =>
    edit({ ...state, zoneProjectsFilter: nextFilter });
  const [columnSizes, setColumnSizes] = React.useState(
    projectListingColumnSizes,
  );

  const onColumnSort = useCallback(
    (columnKey: string, direction: "asc" | "desc" | null) => {
      if (!direction) {
        const defaultSort = m[SortOptions.Default];
        setSort(SortOptions.Default);
        setCurrentSortValue(defaultSort);
        setCurrentSortState({ columnKey: null, direction: null });
        retrieveProjects({
          filter,
          sort: defaultSort,
          index: 0,
          size: 10,
        });
        return;
      }

      if (isCustomCriteria(columnKey, template)) {
        const customSort = buildCustomSort(columnKey, direction);
        setCurrentSortValue(customSort);
        setCurrentSortState({ columnKey, direction });
        retrieveProjects({
          filter,
          sort: customSort,
          index: 0,
          size: 10,
        });
      }
    },
    [filter, template, retrieveProjects, m],
  );

  React.useEffect(() => {
    !isStrategicAxesRequestPending &&
      !isStrategicAxesRequestSuccess &&
      !isStrategicAxesRequestFailure &&
      retrieveStrategicAxes(planzone);
  }, []);

  React.useEffect(() => {
    !isZoneRequestPending &&
      !isZoneRequestSuccess &&
      !isZoneRequestFailure &&
      retrieveZonesIndicators({
        filter: filter,
        sort: "ascendingName",
        size: 1,
        index: 0,
        view: "indicators",
      });
  }, [filter]);

  useMessage(
    ({ type }) =>
      (type === events.assignProjectToPortfolioSucceeded ||
        type === events.createProjectStrategicAxisSucceeded ||
        type === events.editProjectStrategicAxisSucceeded) &&
      retrieveProjects({ sort: currentSortValue, filter, size: 10, index: 0 }),
  );

  return (
    <div className={styles.projects}>
      <div className={styles.header}>
        <Toolbar
          filter={filter}
          sort={sort}
          setFilter={(filter) => {
            setFilter(filter);
            retrieveProjects({
              filter,
              sort: currentSortValue,
              index: 0,
              size: 10,
            });
          }}
          setSort={(sort) => {
            setSort(sort);
            const sortValue = m[sort];
            setCurrentSortValue(sortValue);
            setCurrentSortState({ columnKey: null, direction: null });
            if (sortValue) {
              retrieveProjects({
                filter,
                sort: sortValue,
                index: 0,
                size: 10,
              });
            }
          }}
        />
        <Safeguard
          useCases={[
            UseCases.createProject,
            UseCases.editProjectListingConfiguration,
          ]}
        >
          {({ canCreateProject, canEditProjectListingConfiguration }) =>
            canCreateProject && (
              <div className={styles.row}>
                {canEditProjectListingConfiguration && (
                  <div
                    className={styles.contextualMenuIcon}
                    onClick={() =>
                      pushView({
                        key: UseCases.retrieveProjectListingConfiguration,
                        data: undefined,
                      })
                    }
                  >
                    <CogWheelIcon className={styles.wbsConfigurationIcon} />
                  </div>
                )}
                <ProjectCreationButton zone={props.zone} />
              </div>
            )
          }
        </Safeguard>
      </div>
      <div className={styles.main}>
        <InfiniteScroll className={styles.content} canFetchOnIdle>
          {() => (
            <>
              <VisibilitySwitch
                defaultState={true}
                renderChildren={(isVisible, show, hide) => (
                  <ProjectSection
                    {...indicators}
                    header={(filtered) => (
                      <div className={styles.zoneInfo}>
                        <div
                          className={styles.row}
                          onClick={!isVisible ? show : hide}
                        >
                          <div className={styles.iconBox}>
                            <ListToggleArrow isOpen={isVisible} />
                          </div>
                          <div className={styles.avatar}>
                            <ZoneAvatar
                              name={props.zone.name}
                              color={props.zone.color}
                            />
                          </div>
                          <h2 className={styles.name}>
                            {props.zone.name} (
                            {indicators.totalNumberOfProjectsInZone})
                          </h2>
                        </div>
                        <div className={styles.contextualMenu}>
                          <SecureContextualMenu
                            items={[
                              {
                                name: t("zonePage.projects.editZone"),
                                renderIcon: (className: string) => (
                                  <PenIcon
                                    className={classNames(
                                      className,
                                      styles.menuIcon,
                                    )}
                                  />
                                ),
                                useCase: UseCases.editZone,
                                act: () =>
                                  pushView({
                                    key: UseCases.editZone,
                                    data: props.zone,
                                  }),
                              },
                              {
                                name: t("zonePage.projects.removeZone"),
                                color: "#DB5F63FF",
                                useCase: UseCases.removeZone,
                                renderIcon: (className: string) => (
                                  <TrashIcon
                                    className={classNames(
                                      className,
                                      styles.removeIcon,
                                    )}
                                  />
                                ),
                                act: () =>
                                  pushView({
                                    key: UseCases.removeZone,
                                    data: props.zone,
                                  }),
                              },
                            ]}
                          />
                        </div>
                      </div>
                    )}
                    columnSizes={columnSizes}
                    loader={isRequestPending && <Loader />}
                    projects={projects.map((p) => ({ ...p, zone: props.zone }))}
                    filter={filter}
                    isOpen={isVisible}
                    onColumnSort={onColumnSort}
                    currentSort={currentSortState}
                    resizeCol={(key, size) =>
                      setColumnSizes((sizes) => ({
                        ...sizes,
                        [key]: size,
                      }))
                    }
                    resizeDone={() =>
                      edit({
                        ...state,
                        projectListingColumnSizes: columnSizes,
                      })
                    }
                  />
                )}
              />
              <Flag
                name={props.zone.uuid}
                retrieveItems={get}
                isDisabled={!canRetrieveNextProjects || isRequestPending}
              />
            </>
          )}
        </InfiniteScroll>
      </div>
      <div className={styles.exportButton}>
        <ExportButton
          sort={currentSortValue}
          filter={{ ...props.filter, start: 0 }}
          zone={props.zone}
        />
      </div>
    </div>
  );
}

export { ProjectsPage };
